import { useEffect, useMemo, useState } from "react";
import { articles, type Article } from "../../../mocks/guaData";
import Reveal from "./Reveal";
import SectionHead from "./SectionHead";

const cats = ["全部", ...Array.from(new Set(articles.map((a) => a.category)))];

export default function DeepDive() {
  const [active, setActive] = useState("全部");
  const [selected, setSelected] = useState<Article | null>(null);

  const list = useMemo(
    () => (active === "全部" ? articles : articles.filter((a) => a.category === active)),
    [active]
  );

  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selected]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section id="deep" className="bg-background-50 py-16 md:py-20">
      <div className="mx-auto max-w-[1200px] px-4 md:px-6">
        <SectionHead
          eyebrow="深扒专栏 · 深度解析"
          title={
            <>
              把每一个瓜，
              <span className="text-primary-500">剖开来讲</span>
            </>
          }
          desc="不造谣、不煽动，只从传播机制出发还原运作逻辑。点击任意卡片即可展开完整文章阅读。"
        />

        <Reveal>
          <div className="mb-8 flex flex-wrap items-center gap-2">
            {cats.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActive(c)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  active === c
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {list.map((a, i) => (
            <Reveal key={a.id} delay={i * 60}>
              <button
                type="button"
                onClick={() => setSelected(a)}
                className="group flex h-full w-full flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 text-left transition-colors hover:border-primary-200"
              >
                <div className="relative h-48 w-full overflow-hidden bg-background-100">
                  <img
                    src={a.cover}
                    alt={`${a.title} 深度解析配图`}
                    title={a.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 whitespace-nowrap rounded-full bg-primary-500/95 px-3 py-1 text-xs font-semibold text-background-50">
                    {a.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span>{a.date}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line" aria-hidden="true"></i>
                      {a.readMin}
                    </span>
                  </div>
                  <h3 className="mt-2 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                    {a.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-600">
                    展开阅读
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-foreground-950/60 p-4 backdrop-blur-sm"
          onClick={() => setSelected(null)}
          role="dialog"
          aria-modal="true"
          aria-label={selected.title}
        >
          <article
            className="relative max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-background-200 bg-background-50 p-6 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelected(null)}
              aria-label="关闭阅读"
              className="sticky top-0 float-right flex h-10 w-10 items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-600"
            >
              <i className="ri-close-line text-xl" aria-hidden="true"></i>
            </button>
            <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-quill-pen-line" aria-hidden="true"></i>
              {selected.category}
            </span>
            <h3 className="mt-4 pr-12 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
              {selected.title}
            </h3>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span>发布于 {selected.date}</span>
              <span>阅读约 {selected.readMin}</span>
              <span className="text-primary-600">吃瓜透视局</span>
            </div>
            <div className="mt-6 border-t border-background-200 pt-6">
              {selected.body.map((p, idx) => (
                <p key={idx} className="mb-4 text-[15px] leading-8 text-foreground-700">
                  {p}
                </p>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3 rounded-xl bg-background-100 p-4 text-sm text-foreground-600">
              <i className="ri-lightbulb-flash-line text-primary-500" aria-hidden="true"></i>
              记住：看瓜先看节奏，下结论前让子弹飞一会儿，不站队、只还原。
            </div>
          </article>
        </div>
      )}
    </section>
  );
}